import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { CreditCard, Plus, Edit, Archive, CheckCircle2, XCircle, Users, Star, Save, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import api from '../../lib/api';
import { toast } from 'sonner';

const AdminPlans = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        price: 0,
        duration: 'event',
        guests_limit: 150,
        features: '',
        status: 'active'
    });

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await api.get('/admin/plans');
            setPlans(response.data);
        } catch (error) {
            toast.error('Error al cargar planes');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (plan = null) => {
        if (plan) {
            setEditingPlan(plan);
            setFormData({
                ...plan,
                features: plan.features.join('\n')
            });
        } else {
            setEditingPlan(null);
            setFormData({
                id: '',
                name: '',
                price: 0,
                duration: 'event',
                guests_limit: 150,
                features: '',
                status: 'active'
            });
        }
        setIsDialogOpen(true);
    };

    const handleSavePlan = async () => {
        if (!formData.id || !formData.name) {
            toast.error('Nombre e ID son obligatorios');
            return;
        }

        setFormLoading(true);
        const planToSave = {
            ...formData,
            price: parseFloat(formData.price),
            guests_limit: parseInt(formData.guests_limit),
            features: formData.features.split('\n').filter(f => f.trim() !== ''),
            created_at: editingPlan ? editingPlan.created_at : new Date().toISOString()
        };

        try {
            if (editingPlan) {
                await api.put(`/admin/plans/${editingPlan.id}`, planToSave);
                toast.success('Plan actualizado');
            } else {
                await api.post('/admin/plans', planToSave);
                toast.success('Plan creado');
            }
            setIsDialogOpen(false);
            fetchPlans();
        } catch (error) {
            toast.error('Error al guardar plan');
        } finally {
            setFormLoading(false);
        }
    };

    const toggleStatus = async (plan) => {
        const newStatus = plan.status === 'active' ? 'archived' : 'active';
        try {
            await api.put(`/admin/plans/${plan.id}`, { ...plan, status: newStatus });
            toast.success(`Plan ${newStatus === 'active' ? 'activado' : 'archivado'}`);
            fetchPlans();
        } catch (error) {
            toast.error('Error al cambiar estado');
        }
    };

    const seedPlans = async () => {
        const defaultPlans = [
            {
                id: 'basic',
                name: 'Básico',
                price: 0,
                duration: 'event',
                guests_limit: 50,
                features: ['Invitación Digital', 'RSVP Básico', 'Mapa del Evento'],
                status: 'active',
                created_at: new Date().toISOString()
            },
            {
                id: 'premium',
                name: 'Premium',
                price: 49.99,
                duration: 'event',
                guests_limit: 150,
                features: ['Todo lo del Básico', 'Galería de Fotos', 'Música de Fondo', 'Lista de Regalos'],
                status: 'active',
                created_at: new Date().toISOString()
            },
            {
                id: 'pro',
                name: 'Pro / Agencia',
                price: 99.99,
                duration: 'monthly',
                guests_limit: 1000,
                features: ['Múltiples Eventos', 'Soporte Prioritario', 'Sin Marca de Agua', 'WhatsApp Masivo'],
                status: 'active',
                created_at: new Date().toISOString()
            }
        ];

        try {
            for (const p of defaultPlans) {
                await api.post('/admin/plans', p);
            }
            toast.success('Planes base creados');
            fetchPlans();
        } catch (error) {
            toast.error('Error al inicializar planes');
        }
    };

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="text-sm font-medium text-slate-500">Gestión de Oferta Comercial</h3>
                        <p className="text-2xl font-bold text-slate-800">Planes y Tarifas</p>
                    </div>
                    <div className="flex gap-2">
                        {plans.length === 0 && (
                            <Button variant="outline" onClick={seedPlans}>Inicializar Base</Button>
                        )}
                        <Button className="bg-wedding-primary" onClick={() => handleOpenDialog()}>
                            <Plus className="h-4 w-4 mr-2" /> Crear Plan
                        </Button>
                    </div>
                </div>

                <div className="grid gap-6 md:grid-cols-3">
                    {loading ? (
                        [1, 2, 3].map(i => <div key={i} className="h-64 bg-slate-100 rounded-xl animate-pulse"></div>)
                    ) : plans.length > 0 ? (
                        plans.map((plan) => (
                            <Card key={plan.id} className={`${plan.status === 'archived' ? 'opacity-60 bg-slate-50' : ''} border-2 hover:border-wedding-primary/50 transition-all`}>
                                <CardHeader>
                                    <div className="flex justify-between items-start">
                                        <CardTitle className="text-xl font-heading text-wedding-text">{plan.name}</CardTitle>
                                        {plan.status === 'active' ? (
                                            <Badge className="bg-green-100 text-green-700 hover:bg-green-200">Activo</Badge>
                                        ) : (
                                            <Badge variant="outline" className="bg-slate-100/50">Archivado</Badge>
                                        )}
                                    </div>
                                    <CardDescription>
                                        {plan.duration === 'event' ? 'Por evento individual' : 'Suscripción mensual'}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-3xl font-bold font-heading text-wedding-text">${plan.price}</span>
                                        <span className="text-sm text-slate-500">USD / {plan.duration === 'event' ? 'boda' : 'mes'}</span>
                                    </div>

                                    <div className="space-y-2 border-t pt-4">
                                        <div className="flex items-center text-sm font-semibold text-slate-700">
                                            <Users className="h-4 w-4 mr-2 text-wedding-primary" />
                                            Límite: {plan.guests_limit} invitados
                                        </div>
                                        {plan.features.map((feature, i) => (
                                            <div key={i} className="flex items-start text-sm text-slate-600">
                                                <CheckCircle2 className="h-4 w-4 mr-2 text-green-500 mt-0.5 shrink-0" />
                                                <span>{feature}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex gap-2 pt-4">
                                        <Button variant="outline" size="sm" className="flex-1 border-wedding-border text-wedding-text hover:bg-wedding-accent" onClick={() => handleOpenDialog(plan)}>
                                            <Edit className="h-4 w-4 mr-2" /> Editar
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className={`flex-1 ${plan.status === 'active' ? 'text-slate-500 hover:text-red-500' : 'text-green-600'}`}
                                            onClick={() => toggleStatus(plan)}
                                        >
                                            <Archive className="h-4 w-4 mr-2" /> {plan.status === 'active' ? 'Archivar' : 'Recuperar'}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    ) : (
                        <div className="col-span-3 py-12 text-center bg-white rounded-xl border-2 border-dashed border-slate-200">
                            <CreditCard className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                            <p className="text-lg font-medium text-slate-600">Aún no hay planes definidos</p>
                            <Button variant="link" onClick={seedPlans} className="text-wedding-primary">
                                Crear planes por defecto
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Plan Editor Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">
                            {editingPlan ? 'Editar Plan' : 'Crear Nuevo Plan'}
                        </DialogTitle>
                        <DialogDescription>
                            Configura las condiciones y precio del plan comercial.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="planId">ID único (slug)</Label>
                                <Input
                                    id="planId"
                                    placeholder="ej: premium-anual"
                                    disabled={!!editingPlan}
                                    value={formData.id}
                                    onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="name">Nombre comercial</Label>
                                <Input
                                    id="name"
                                    placeholder="ej: Plan VIP"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="price">Precio (USD)</Label>
                                <Input
                                    id="price"
                                    type="number"
                                    step="0.01"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="duration">Frecuencia</Label>
                                <Select
                                    value={formData.duration}
                                    onValueChange={(v) => setFormData({ ...formData, duration: v })}
                                >
                                    <SelectTrigger id="duration">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="event">Pago único (Boda)</SelectItem>
                                        <SelectItem value="monthly">Suscripción Mensual</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="limit">Límite de Invitados</Label>
                            <Input
                                id="limit"
                                type="number"
                                value={formData.guests_limit}
                                onChange={(e) => setFormData({ ...formData, guests_limit: e.target.value })}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="features">Características (una por línea)</Label>
                            <Textarea
                                id="features"
                                rows={4}
                                placeholder="Invitación Digital\nRSVP\nGalería..."
                                value={formData.features}
                                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                                className="resize-none font-sans text-sm"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                        <Button
                            className="bg-wedding-primary hover:bg-wedding-primary-dark"
                            onClick={handleSavePlan}
                            disabled={formLoading}
                        >
                            {formLoading ? 'Guardando...' : (editingPlan ? 'Actualizar Cambios' : 'Crear Plan')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};

export default AdminPlans;

